/**
 * Validates a user-supplied redirect target to prevent open redirect attacks.
 *
 * Only same-origin redirects are allowed. The supplied value is resolved
 * against the current origin; any absolute or protocol-relative URL that
 * points to a different origin (e.g. "https://evil.example",
 * "//evil.example"), uses a dangerous scheme (e.g. "javascript:"), or resolves
 * to a protocol-relative path that escapes the origin (e.g.
 * "https://<origin>//evil.example", which yields the pathname "//evil.example")
 * is rejected. Relative paths such as "/landing/deleteAccount" are returned
 * normalised to a path that is guaranteed to stay on the current origin.
 *
 * See OWASP "Unvalidated Redirects and Forwards" / open redirect guidance:
 * https://owasp.org/www-community/attacks/open_redirect
 *
 * @returns a safe, same-origin redirect path, or null when the value is
 *          missing, blank, or points off-site.
 */
export function getSafeRedirectUrl(
    redirectUrl: string | null | undefined,
    origin: string = window.location.origin,
): string | null {
    if (!redirectUrl || !redirectUrl.trim()) {
        return null;
    }

    let resolved: URL;
    try {
        // Resolve the redirect against the current origin. A relative path
        // (e.g. "/landing/deleteAccount") inherits `origin`, while an absolute
        // or protocol-relative URL (e.g. "https://evil.example", "//evil.example")
        // keeps/derives its own origin — which the check below then compares
        // against `origin`. Values that aren't parseable as a URL throw and are
        // rejected.
        resolved = new URL(redirectUrl, origin);
    } catch (error) {
        return null;
    }

    if (resolved.origin !== origin) {
        return null;
    }

    // Reconstruct the destination from its path components only. This strips
    // the scheme and host.
    const safePath = resolved.pathname + resolved.search + resolved.hash;

    // The origin check above is not sufficient on its own: a same-origin input
    // such as "https://<origin>//evil.example" parses with the correct origin
    // but yields a protocol-relative pathname ("//evil.example"). Assigning that
    // raw pathname to `window.location.href` navigates off-origin. Re-resolve the
    // reconstructed path and only accept it if the FINAL destination is still
    // same-origin, which rejects protocol-relative ("//", "/\") paths.
    let finalOrigin: string;
    try {
        finalOrigin = new URL(safePath, origin).origin;
    } catch (error) {
        return null;
    }
    if (finalOrigin !== origin) {
        return null;
    }

    return safePath;
}
